import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Modal,
  Switch,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import CommonTable from 'components/CommonTable/commonTable';
import TimesheetBox, {
  TimesheetBoxDataRow,
  TimesheetBoxGroup,
  TimesheetBoxRow,
} from 'components/TimesheetBox/timesheetBox';
import moment from 'moment';
import React from 'react';
import { useAppSelector } from 'store/hooks';
import { DisassociatedGroup, DisassociatedRow } from 'types';

import { disassociatedStyles as styles } from './styles';
import { useDisassociations } from './useDisassociations';

const weekHours = (week: DisassociatedRow) =>
  Number(week.adjustmentHours) > 0
    ? Number(week.adjustmentHours)
    : Number(week.hours);

/** The date range is what actually identifies an assignment - a consultant can hold several on
 * the same client and project, and the name alone does not tell them apart. */
const assignmentWindow = (week?: DisassociatedRow) => {
  if (!week?.originalEndDate) {
    return '-';
  }
  const end = moment(week.originalEndDate).format('DD MMM YYYY');
  return week.originalStartDate
    ? `${moment(week.originalStartDate).format('DD MMM YYYY')} - ${end}`
    : `ended ${end}`;
};

const weekLabel = (week: { periodFrom: string; periodTo: string }) =>
  `${moment(week.periodFrom).format('DD MMM')} - ${moment(week.periodTo).format(
    'DD MMM YYYY',
  )}`;

const leftBehindCount = (group: DisassociatedGroup | null) =>
  group ? group.weeks.length - (group.resolvableCount ?? 0) : 0;

/** The weeks that actually made it: those that had a destination, less any the restore refused
 * because a live timesheet already held that slot. */
const restoredWeekLabels = (
  group: DisassociatedGroup,
  blocked: { periodFrom: string; periodTo: string }[],
) => {
  const blockedFrom = new Set(
    (blocked ?? []).map((week) => moment(week.periodFrom).format('YYYY-MM-DD')),
  );
  const labels = group.weeks
    .filter(
      (week) =>
        week.destinationProjectConsultantId &&
        !blockedFrom.has(moment(week.periodFrom).format('YYYY-MM-DD')),
    )
    .map(weekLabel);
  return labels.length ? labels.join(', ') : '-';
};

const DetailField = (props: { label: string; value: string }) => (
  <Typography component={'div'} sx={styles.detailField}>
    <Typography sx={styles.detailLabel}>{props.label}</Typography>
    <Typography sx={styles.detailValue}>{props.value}</Typography>
  </Typography>
);

const ReviewModal = (props: {
  group: DisassociatedGroup | null;
  onClose: () => void;
}) => {
  const { group, onClose } = props;
  if (!group) {
    return null;
  }

  const resolvable = group.resolvableCount ?? 0;
  const stuck = group.weeks.length - resolvable;
  const [first] = group.weeks;

  return (
    <Modal open onClose={onClose} sx={styles.modal}>
      <Box sx={styles.modalBox}>
        <Typography component={'div'} sx={styles.modalHead}>
          <Typography sx={styles.modalTitle}>
            Disassociated Timesheets
          </Typography>
          <Button onClick={onClose} sx={styles.modalClose}>
            Close
          </Button>
        </Typography>

        <Typography component={'div'} sx={styles.detailGrid}>
          <DetailField label="Consultant" value={group.consultantName || '-'} />
          <DetailField label="Company" value={group.ultraStaffDbName} />
          <DetailField
            label="Project the hours came off"
            value={group.projectName || '-'}
          />
          <DetailField label="Assignment" value={assignmentWindow(first)} />
          <DetailField
            label="Disassociated on"
            value={moment(group.disassociatedAt).format('DD MMM YYYY')}
          />
          <DetailField
            label="Total hours held"
            value={`${Number(group.totalHours ?? 0).toFixed(2)} hrs`}
          />
        </Typography>

        <Typography component={'div'} sx={styles.reasonBlock}>
          <Typography sx={styles.detailLabel}>Reason</Typography>
          <Typography sx={styles.reasonText}>
            {group.reason || 'No reason was recorded.'}
          </Typography>
        </Typography>

        <Typography sx={styles.modalSummary}>
          {resolvable > 0
            ? `${String(resolvable)} of ${String(
                group.weeks.length,
              )} week(s) now have a destination project. ${
                stuck > 0
                  ? `The remaining ${String(
                      stuck,
                    )} have none and will stay on this tab.`
                  : ''
              }`
            : `None of these ${String(
                group.weeks.length,
              )} week(s) have a destination project yet. They stay on this tab until one appears.`}
        </Typography>

        <CommonTable
          headers={[
            'Week',
            'Hours',
            'Rate',
            'Amount',
            'Destination',
            'Starts',
          ]}>
          {group.weeks.map((week: DisassociatedRow) => (
            <TableRow key={week.id}>
              <TableCell sx={styles.tableCell}>
                {`${moment(week.periodFrom).format('DD MMM')} - ${moment(
                  week.periodTo,
                ).format('DD MMM YYYY')}`}
              </TableCell>
              <TableCell sx={styles.tableCell}>
                {weekHours(week).toFixed(2)}
              </TableCell>
              <TableCell sx={styles.tableCell}>
                {week.payRate ? `$${Number(week.payRate).toFixed(2)}` : '-'}
              </TableCell>
              <TableCell sx={styles.tableCell}>
                {week.payRate
                  ? `$${(weekHours(week) * Number(week.payRate)).toFixed(2)}`
                  : '-'}
              </TableCell>
              <TableCell sx={styles.tableCell}>
                {week.destinationProjectConsultantId ? (
                  <Chip
                    color="success"
                    label={week.destinationProjectName || 'Available'}
                    size="small"
                    sx={styles.chip}
                  />
                ) : (
                  <Typography sx={styles.noDestination}>
                    No destination
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={styles.tableCell}>
                {week.destinationStartDate
                  ? moment(week.destinationStartDate).format('DD MMM YYYY')
                  : '-'}
              </TableCell>
            </TableRow>
          ))}
        </CommonTable>
      </Box>
    </Modal>
  );
};

const DisassociatedTimesheets = (): React.ReactElement => {
  const { companyStatusQueue }: any = useAppSelector(
    (store: any) => store.userLogin,
  );
  const organization =
    companyStatusQueue === 'smartworks' || companyStatusQueue === 'itech'
      ? String(companyStatusQueue)
      : undefined;

  const {
    closeReview,
    dismissResult,
    groups,
    openReview,
    resolvableGroups,
    resolve,
    state,
    toggleWithDestinationOnly,
    totalGroups,
  } = useDisassociations({ organization });

  // One open at a time, as the timesheet page does.
  const [expanded, setExpanded] = React.useState<string>('');

  return (
    <Box sx={styles.page}>
      <Typography component={'div'} sx={styles.headerRow}>
        <Typography sx={styles.title}>Disassociated Timesheets</Typography>
        <Typography sx={styles.subTitle}>
          Timesheets removed from an assignment that had no destination project.
          A project appearing later is shown here so the hours can be picked up.
        </Typography>
      </Typography>

      <Typography component={'div'} sx={styles.toolbar}>
        <Typography sx={styles.counts}>
          {`${String(totalGroups)} assignment(s)`}
          {resolvableGroups > 0
            ? ` · ${String(resolvableGroups)} with a destination`
            : ''}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={state.withDestinationOnly}
              onChange={toggleWithDestinationOnly}
              size="small"
            />
          }
          label={
            <Typography sx={styles.switchLabel}>
              Only those with a destination
            </Typography>
          }
        />
      </Typography>

      {state.resolveResult && (
        <Alert severity="success" onClose={dismissResult} sx={styles.alert}>
          <Typography sx={styles.alertHead}>
            {`${String(
              state.resolveResult.restoredCount,
            )} timesheet(s) restored${
              state.resolveContext
                ? ` for ${String(
                    state.resolveContext.consultantName || 'this consultant',
                  )}`
                : ''
            }.`}
          </Typography>
          {state.resolveContext && (
            <>
              <Typography sx={styles.alertLine}>
                {`${String(state.resolveContext.projectName || '-')} · ${String(
                  state.resolveContext.ultraStaffDbName,
                )} · ${assignmentWindow(state.resolveContext.weeks[0])}`}
              </Typography>
              <Typography sx={styles.alertLine}>
                {`Weeks moved: ${restoredWeekLabels(
                  state.resolveContext,
                  state.resolveResult.blockedWeeks,
                )}`}
              </Typography>
            </>
          )}
          <Typography sx={styles.alertLine}>
            {state.resolveResult.queueCreated
              ? 'A new retro pay item was raised on the Queue - move the hours from there.'
              : `A retro pay item for this assignment was already waiting on the Queue, dated ${moment(
                  state.resolveResult.queueDate,
                ).format(
                  'DD MMM YYYY',
                )}. It was reused rather than duplicated, so look for that date rather than today.`}
          </Typography>
          {leftBehindCount(state.resolveContext) > 0 && (
            <Typography sx={styles.alertLine}>
              {`${String(
                leftBehindCount(state.resolveContext),
              )} timesheet(s) had no destination project and were left here. They stay on this tab until a project appears for them.`}
            </Typography>
          )}
          {state.resolveResult.blockedWeeks?.length ? (
            <Typography sx={styles.alertLine}>
              {`${String(
                state.resolveResult.blockedWeeks.length,
              )} could not be restored - a timesheet already exists for that week.`}
            </Typography>
          ) : null}
        </Alert>
      )}
      {state.resolveError && (
        <Alert severity="error" onClose={dismissResult} sx={styles.alert}>
          {state.resolveError}
        </Alert>
      )}
      {state.error && (
        <Alert severity="error" sx={styles.alert}>
          {state.error}
        </Alert>
      )}

      {state.loading ? (
        <Typography component={'div'} sx={styles.centered}>
          <CircularProgress size={26} />
        </Typography>
      ) : !groups.length ? (
        <Typography component={'div'} sx={styles.emptyBox}>
          <Typography sx={styles.emptyText}>No Records Found</Typography>
        </Typography>
      ) : (
        <TimesheetBoxGroup>
          {groups.map((group: DisassociatedGroup) => (
            <TimesheetBox
              key={group.projectConsultantId}
              expanded={expanded === group.projectConsultantId}
              onToggle={() =>
                setExpanded(
                  expanded === group.projectConsultantId
                    ? ''
                    : group.projectConsultantId,
                )
              }
              totalLabel="Total Hours"
              totalValue={Number(group.totalHours ?? 0).toFixed(2)}
              label={
                <>
                  <Typography sx={styles.consultantName}>
                    {group.consultantName || '-'}
                  </Typography>
                  <Typography sx={styles.projectName}>
                    {`${group.projectName || '-'} · ${
                      group.ultraStaffDbName
                    } · ${assignmentWindow(group.weeks[0])}`}
                  </Typography>
                  <Typography sx={styles.metaLine}>
                    {`${String(
                      group.weeks.length,
                    )} week(s) · disassociated ${moment(
                      group.disassociatedAt,
                    ).format('DD MMM YYYY')}`}
                  </Typography>
                  {group.reason && (
                    <Typography sx={styles.reasonLine}>
                      <Typography component="span" sx={styles.reasonLabel}>
                        Reason:
                      </Typography>
                      {` ${group.reason}`}
                    </Typography>
                  )}
                  {(group.resolvableCount ?? 0) > 0 && (
                    <Chip
                      color="success"
                      label={`${String(group.resolvableCount)} of ${String(
                        group.weeks.length,
                      )} have a destination`}
                      size="small"
                      variant="outlined"
                      sx={styles.chip}
                    />
                  )}
                </>
              }>
              {group.weeks.map((week: DisassociatedRow) => (
                <TimesheetBoxRow
                  key={week.id}
                  headers={['Project', 'Hours', 'Rate', 'Destination']}
                  label={
                    <>
                      <em>{moment(week.periodFrom).format('MMM')}</em>{' '}
                      {moment(week.periodFrom).format('DD')} -{' '}
                      {moment(week.periodTo).format('DD')}
                    </>
                  }>
                  <TableBody>
                    <TimesheetBoxDataRow>
                      <TableCell sx={styles.tableCell}>
                        {group.projectName || '-'}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {weekHours(week).toFixed(2)}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {week.payRate
                          ? `$${Number(week.payRate).toFixed(2)}`
                          : '-'}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        {week.destinationProjectConsultantId ? (
                          <Chip
                            color="success"
                            label={week.destinationProjectName || 'Available'}
                            size="small"
                            sx={styles.chip}
                          />
                        ) : (
                          <Typography sx={styles.noDestination}>
                            No destination
                          </Typography>
                        )}
                      </TableCell>
                    </TimesheetBoxDataRow>
                  </TableBody>
                </TimesheetBoxRow>
              ))}

              <Typography component={'div'} sx={styles.actionRow}>
                <Typography sx={styles.actionNote}>
                  {(group.resolvableCount ?? 0) > 0
                    ? `Restores the ${String(
                        group.resolvableCount,
                      )} week(s) with a destination and creates a retro pay item so the hours can be moved from the Queue.${
                        group.weeks.length - (group.resolvableCount ?? 0) > 0
                          ? ` The other ${String(
                              group.weeks.length - (group.resolvableCount ?? 0),
                            )} stay here - they still have nowhere to go.`
                          : ''
                      }`
                    : 'No project has appeared for these week(s) yet, so there is nothing to restore. They stay here and pick up a destination automatically if one is created.'}
                </Typography>
                <Typography component={'div'} sx={styles.actionButtons}>
                  <Button
                    onClick={() => openReview(group)}
                    variant="outlined"
                    sx={styles.reviewButton}>
                    Review Timesheets
                  </Button>
                  {(group.resolvableCount ?? 0) > 0 && (
                    <Button
                      disabled={state.resolving === group.projectConsultantId}
                      onClick={() => resolve(group)}
                      variant="contained"
                      sx={styles.resolveButton}>
                      {state.resolving === group.projectConsultantId
                        ? 'Working...'
                        : 'Create queue item to resolve'}
                    </Button>
                  )}
                </Typography>
              </Typography>
            </TimesheetBox>
          ))}
        </TimesheetBoxGroup>
      )}

      <ReviewModal group={state.reviewing} onClose={closeReview} />
    </Box>
  );
};

export default DisassociatedTimesheets;
